import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from './Button';
import { cn } from '../../lib/utils';
import { zoomIn, zoomOut, zoomPercentLabel } from './zoomable-pane.utils';

interface ZoomablePaneProps {
  children: ReactNode;
  className?: string;
}

export function ZoomablePane({ children, className }: ZoomablePaneProps) {
  const [zoom, setZoom] = useState(1);
  const viewportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      setZoom((current) => (event.deltaY < 0 ? zoomIn(current) : zoomOut(current)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return (
    <div className={cn('relative flex min-h-0 flex-1 flex-col overflow-hidden', className)}>
      <div className="absolute top-2 right-2 z-20 flex items-center gap-0.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 p-1 shadow-sm backdrop-blur-sm">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setZoom((current) => zoomOut(current))}
          aria-label="Zoom out"
          title="Zoom out"
        >
          <ZoomOut className="w-4 h-4" />
        </Button>
        <span className="min-w-[2.75rem] text-center text-[11px] tabular-nums text-gray-600 dark:text-gray-400">
          {zoomPercentLabel(zoom)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setZoom((current) => zoomIn(current))}
          aria-label="Zoom in"
          title="Zoom in"
        >
          <ZoomIn className="w-4 h-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setZoom(1)}
          aria-label="Reset zoom"
          title="Reset zoom"
        >
          <Maximize2 className="w-4 h-4" />
        </Button>
      </div>
      <div
        ref={viewportRef}
        className="min-h-0 flex-1 overflow-auto"
        title="Ctrl or ⌘ + scroll wheel to zoom"
      >
        <div
          className="origin-top-left p-2"
          style={{
            transform: `scale(${zoom})`,
            width: `${100 / zoom}%`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
